import type {
  AuthorProfile,
  CollectionSummary,
  Leaderboard,
  Page,
  PromptCard,
  SupportMessage,
  SupportThreadView,
} from '@promptvault/shared';
import { apiFetch } from './client';

/* ── Leaderboard ─────────────────────────────────────────────────────────── */

export function fetchLeaderboard(period: 'week' | 'all' = 'week'): Promise<Leaderboard> {
  return apiFetch<Leaderboard>('/leaderboard', { params: { period } });
}

/* ── Authors ─────────────────────────────────────────────────────────────── */

export function fetchAuthor(key: string): Promise<AuthorProfile> {
  return apiFetch<AuthorProfile>(`/authors/${encodeURIComponent(key)}`);
}

export function fetchAuthorPrompts(key: string, cursor?: string): Promise<Page<PromptCard>> {
  return apiFetch<Page<PromptCard>>(`/authors/${encodeURIComponent(key)}/prompts`, {
    params: { cursor },
  });
}

/* ── Collections ─────────────────────────────────────────────────────────── */

export function fetchCollections(): Promise<CollectionSummary[]> {
  return apiFetch<CollectionSummary[]>('/me/collections');
}

export function createCollection(input: {
  name: string;
  description?: string;
  isPublic?: boolean;
}): Promise<CollectionSummary> {
  return apiFetch<CollectionSummary>('/me/collections', {
    method: 'POST',
    body: JSON.stringify({ isPublic: false, ...input }),
  });
}

export function fetchCollectionPrompts(id: string, cursor?: string): Promise<Page<PromptCard>> {
  return apiFetch<Page<PromptCard>>(`/me/collections/${id}`, { params: { cursor } });
}

export function deleteCollection(id: string): Promise<{ deleted: boolean }> {
  return apiFetch(`/me/collections/${id}`, { method: 'DELETE' });
}

export function addToCollection(id: string, promptId: string): Promise<{ itemCount: number }> {
  return apiFetch(`/me/collections/${id}/items`, {
    method: 'POST',
    body: JSON.stringify({ promptId }),
  });
}

export function removeFromCollection(id: string, promptId: string): Promise<{ itemCount: number }> {
  return apiFetch(`/me/collections/${id}/items/${promptId}`, { method: 'DELETE' });
}

/* ── Support ─────────────────────────────────────────────────────────────── */

export function fetchSupportThread(): Promise<SupportThreadView> {
  return apiFetch<SupportThreadView>('/support/messages');
}

export function sendSupportMessage(body: string): Promise<SupportMessage> {
  return apiFetch<SupportMessage>('/support/messages', {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}
